import * as React from 'react'
import { ThemeProvider, useTheme } from 'styled-components'
import { offeredServiceResourcesList, RansackQuery } from '../../../services/models/offered-service-resources.service'
import { I18nService } from '../../../services/utils/i18n.service'
import ErrorBoxComponent from '../../shared/error-box.component'
import LoadingComponent from '../../shared/loading.component'
import SelectableListComponent from '../../shared/selectable-list.component'
import OfferedServiceResourcesFilterComponent from './offered-service-resources-filter.component'
import { OfferedServiceBase } from '../../../services/json-schemas/base/offered_service'
import { ServiceDeliveryLocationBase } from '../../../services/json-schemas/base/service_delivery_location'
import { OfferedServiceResourceBase } from '../../../services/json-schemas/base/offered_service_resource'
import { OfferedServiceCombinationBase } from '../../../services/json-schemas/base/offered_service_combination'
import { usePagination } from '../../../hooks/use-pagination'

type OfferedServiceResource = (Pick<OfferedServiceResourceBase, 'id' | 'ref' | 'working_days' | 'multi_opening_hours'> & {
  offered_service_stock: { offered_service_combination: Pick<OfferedServiceCombinationBase, 'name'> }
})

interface Props {
  query?: RansackQuery
  offeredServices: Pick<OfferedServiceBase, 'id' | 'name' | 'offered_service_type'>[]
  availableForBody?: RansackQuery['available_for']
  serviceDeliveryLocations?: Pick<ServiceDeliveryLocationBase, 'id' | 'name' | 'inline_address'>[]
  onResult: (_: OfferedServiceResource) => void
  onCancel: () => void
}

const i18nScoped = new I18nService(require('./offered-service-resources-picker-modal.component.yml'))

export default function OfferedServiceResourcesPickerModalComponent ({ availableForBody, offeredServices, serviceDeliveryLocations, onResult, ...otherProps }: Props) {
  const theme = useTheme()
  const [errors, setErrors] = React.useState<string[]>([])

  const { hasMore, isLoading, query, entities: offeredServiceResources_, setQuery, load } = usePagination<OfferedServiceResource, RansackQuery>({
    loader: query => offeredServiceResourcesList(query).then(({ offered_service_resources, has_more }) => ({ entities: offered_service_resources, hasMore: has_more })),
    defaultQuery: otherProps.query,
    setErrors
  })

  const offeredServiceResources = React.useMemo(() => {
    return offeredServiceResources_?.slice().sort((a, b) => a.ref.localeCompare(b.ref))
  }, [offeredServiceResources_])

  return <div style={{ padding: '1em' }}>
    <ErrorBoxComponent fullMessages={ errors } />

    <div className="grid">
      <div className="grid-1-3 u-borderR u-prm">
        <OfferedServiceResourcesFilterComponent
          query={ query }
          availableForBody={ availableForBody }
          offeredServices={ offeredServices }
          serviceDeliveryLocations={ serviceDeliveryLocations }
          onChange={ setQuery }
          onSubmit={ load } />
      </div>

      <div className="grid-2-3">
        <label className="form-label">{ i18nScoped.t('select_one_in_list') }</label>

        <div style={{ maxHeight: '23em', overflowY: 'auto' }}>
          <ThemeProvider theme={{ ...theme, selectableListOnComputerWithOuterBorder: false }}>
            <SelectableListComponent
              name="offeredServiceResource"
              data={ offeredServiceResources }
              value={ null }
              formatter={ resource => <>
                <div style={{ fontWeight: 'bold' }}>{ resource.ref }</div>
                { resource.offered_service_stock.offered_service_combination.name }
              </> }
              onChange={ onResult } />
          </ThemeProvider>

          { isLoading && <LoadingComponent presentation='inline' align='center' /> }

          { !isLoading && !offeredServiceResources.length && <div className="message">
            <span className="icon icon-info"></span>
            <p>{ i18nScoped.t('no_content') }</p>
          </div> }

          { hasMore && <p className="u-txtCenter">
            <a onClick={ () => load(true) }>{ i18nScoped.t('see_more') }</a>
          </p> }
        </div>
      </div>
    </div>
  </div>
}
